import { Controller } from 'react-hook-form'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Form from '@/elements/Form'
import FormInput from '@/elements/FormInput'
import FormFile from '@/elements/FormFile'
import FormSelect from '@/elements/FormSelect'
import useOrganizationIndividualForm from '@/hooks/forms/useOrganizationIndividualForm'
import { statesData, type StateData } from '@/utils/stateData'
import { useGetSubjectGroups, useGetSubjectSpecialties } from '@/api/profile'

function OrganizationIndividualForm({ form, mode, onSubmit }: {
  form: ReturnType<typeof useOrganizationIndividualForm>['form']
  mode: 'add' | 'edit'
  onSubmit: () => void
}) {
  const { data: groups } = useGetSubjectGroups({ assigned: false })
  const groupOptions = groups?.data || []

  const { data: specialities } = useGetSubjectSpecialties()
  const specialtyOptions = specialities?.data || []

  return (
    <Form onSubmit={e => {
      e.preventDefault()
      onSubmit()
    }}>
      <Flex mb={mode === 'edit' ? 3 : undefined}>
        <Controller
          name='avatar'
          control={form.control}
          render={({ field }) => (
            <FormFile
              {...field}
              files={field.value ? [field.value] : []}
              label='Profile Image'
              placeholder='Upload Photo'
              accept='.png, .jpg, .jpeg'
              onChange={e => {
                const file = e.target.files?.[0]
                field.onChange(file)
              }}
              onClear={() => {
                form.setValue('avatar', null)
                form.setValue('previousAvatar', '')
                field.onChange(null)
              }}
              error={form.formState.errors.avatar?.message}
              instructions='JPEG or PNG files supported.'
              preview='avatar'
              previousImageUrl={form.watch('previousAvatar') as string}
            />
          )}
        />
      </Flex>
      <Controller
        name='cover'
        control={form.control}
        render={({ field }) => (
          <FormFile
            {...field}
            files={field.value ? [field.value] : []}
            label='Cover Image'
            placeholder='Upload Photo'
            accept='.png, .jpg, .jpeg'
            onChange={e => {
              const file = e.target.files?.[0]
              field.onChange(file)
            }}
            onClear={() => {
              form.setValue('cover', null)
              form.setValue('previousCover', '')
              field.onChange(null)
            }}
            error={form.formState.errors.cover?.message}
            instructions='Recommended dimensions: 1012x302 pixels. JPEG or PNG files supported.'
            preview='default'
            scale='3.35:1'
            previousImageUrl={form.watch('previousCover') as string}
          />
        )}
      />
      <Flex className='form-group'>
        <Controller
          name='firstName'
          control={form.control}
          render={({ field }) => (
            <FormInput
              {...field}
              label='First Name'
              placeholder='Enter first name'
              dirty={!!form.formState.dirtyFields.firstName}
              error={form.formState.errors.firstName?.message}
            />
          )}
        />
        <Controller
          name='lastName'
          control={form.control}
          render={({ field }) => (
            <FormInput
              {...field}
              label='Last Name'
              placeholder='Enter last name'
              dirty={!!form.formState.dirtyFields.lastName}
              error={form.formState.errors.lastName?.message}
            />
          )}
        />
      </Flex>
      <Controller
        name='email'
        control={form.control}
        render={({ field }) => (
          <FormInput
            {...field}
            type='email'
            label='Email'
            placeholder='Enter email'
            dirty={!!form.formState.dirtyFields.email}
            error={form.formState.errors.email?.message}
            disabled={mode === 'edit'}
          />
        )}
      />
      <Flex className='form-group'>
        <Controller
          name='city'
          control={form.control}
          render={({ field }) => (
            <FormInput
              {...field}
              label='City Location'
              placeholder='Enter city name'
              dirty={!!form.formState.dirtyFields.city}
              error={form.formState.errors.city?.message}
            />
          )}
        />
        <Controller
          name='state'
          control={form.control}
          render={({ field }) => (
            <FormSelect<StateData>
              {...field}
              label='State Location'
              placeholder='Select state'
              options={statesData}
              optionValue={item => item.state}
              optionLabel={item => `${item.state} (${item.stateCode})`}
              onChange={([val]) => field.onChange(val ? val.state : '')}
              values={
                field.value
                  ? statesData.filter(s => s.state === field.value)
                  : []
              }
              dirty={!!form.formState.dirtyFields.state}
              error={form.formState.errors.state?.message}
              filterBy={(option, search) =>
                option.state.toLowerCase().includes(search.toLowerCase()) ||
                option.stateCode.toLowerCase().includes(search.toLowerCase())
              }
            />
          )}
        />
      </Flex>
      <Flex className='form-group'>
        <Controller
          name='groupName'
          control={form.control}
          render={({ field }) => (
            <FormSelect<string>
              {...field}
              label='Group Name'
              placeholder='Select group'
              options={groupOptions.map(g => g.name)}
              values={field.value ? [field.value] : []}
              optionValue={item => item}
              optionLabel={item => item}
              onChange={([val]) => field.onChange(val || '')}
              dirty={!!form.formState.dirtyFields.groupName}
              error={form.formState.errors.groupName?.message}
              filterBy={(option, search) =>
                option.toLowerCase().includes(search.toLowerCase())
              }
              createCustomValue={search => search}
            />
          )}
        />
        <Controller
          name='specialty'
          control={form.control}
          render={({ field }) => (
            <FormSelect<string>
              {...field}
              label='Specialty'
              placeholder='Select specialty'
              options={specialtyOptions.map(s => s)}
              values={field.value ? [field.value] : []}
              optionValue={item => item}
              optionLabel={item => item}
              onChange={([val]) => field.onChange(val || '')}
              dirty={!!form.formState.dirtyFields.specialty}
              error={form.formState.errors.specialty?.message}
              filterBy={(option, search) =>
                option.toLowerCase().includes(search.toLowerCase())
              }
              createCustomValue={search => search}
            />
          )}
        />
      </Flex>
      {mode === 'edit' && (
        <Flex className='form-controls'>
          <Button
            submit
            variant='accent'
            disabled={!form.formState.isDirty}
          >
            Save changes
          </Button>
        </Flex>
      )}
    </Form >
  )
}

export default OrganizationIndividualForm
